/* eslint-disable @angular-eslint/directive-selector */
import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges
} from '@angular/core';
import {TextInputComponent} from '@blocks/input/text-input/text-input.component';
import {Subscription} from 'rxjs';
import {FormControl, NgModel} from '@angular/forms';
import {DateInputComponent} from '@blocks/input/date-input/date-input.component';
import {SignatureInputComponent} from '@blocks/input/signature-input/signature-input.component';
import {FileUploadInputComponent} from '@blocks/input/file-upload-input/file-upload-input.component';
import {BankAccountInputComponent} from '@blocks/input/bank-account-input/bank-account-input.component';
import {CheckboxInputComponent} from '@blocks/input/checkbox-input/checkbox-input.component';
import {RadioInputComponent} from '@blocks/input/radio-input/radio-input.component';
import {filter} from 'rxjs/operators';
import {NavigationService} from '@shared/submission-gizmo/navigation.service';
import {BLOCKS_CONFIG} from '@blocks/blocks.config';
import {DataSetInputComponent} from '@blocks/input/data-set-input/data-set-input.component';
import {DataSetInput} from '@paperlessio/sdk/api/models';

@Directive({
  selector: '[gizmoDestination]',
  exportAs: 'gizmoDestination'
})
export class GizmoDestinationDirective implements OnDestroy, OnChanges, AfterViewInit {
  @Input('gizmoDestination') targetInputComponent: TextInputComponent | DateInputComponent | SignatureInputComponent
    | FileUploadInputComponent | BankAccountInputComponent | CheckboxInputComponent | RadioInputComponent | DataSetInputComponent;
  @Input() gizmoDestinationInput: FormControl | NgModel;

  private navigationService = inject(NavigationService, {optional: true});
  private cdr = inject(ChangeDetectorRef);
  private elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private blocksConfig = inject(BLOCKS_CONFIG);
  private dataSetInputComponent = inject(DataSetInputComponent, {optional: true});

  private subs = new Subscription();
  private changeHooked = false;

  @HostBinding('class.is-current-target') isCurrentTarget: boolean;

  @HostBinding('class.gizmo-destination') get gizmoDestinationClass() {
    return this.blocksConfig.submittable && this.targetInputComponent.block.settings.required;
  }

  // focusin also detects when the focus is in a child of the element. That's important for the radio & checkbox input block
  @HostListener('focusin', ['$event'])
  @HostListener('click', ['$event'])
  focused($event) {
    if(($event instanceof FocusEvent) && this.dataSetInputComponent) {
      $event.stopPropagation();
    }

    this.blocksConfig.submittable
      && !this.blocksConfig.editable
      && !this.targetInputComponent.ineligible
      && !('readonly' in this.targetInputComponent?.block?.settings && this.targetInputComponent?.block?.settings.readonly)
      && this.navigationService?.activateTarget(this.elementRef.nativeElement.id);

    this.navigationService?.fireGizmoStateAfterScroll();
  }

  ngAfterViewInit() {
    if (this.blocksConfig.submittable && this.navigationService) {
      if (this.targetInputComponent?.block) {
        if (!this.targetInputComponent.ineligible) {
          this.navigationService.registerTarget(this.targetInputComponent.block, this.elementRef, this);
        }
      }

      this.subs.add(this.navigationService.gizmoStateAfterScroll
        .pipe(filter(s => s?.currentTarget?.targetElementId != null))
        .subscribe(state => {
          this.isCurrentTarget = this.elementRef.nativeElement.id === state.currentTarget.targetElementId;
          this.cdr.markForCheck();
        }));
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.blocksConfig.submittable || !this.navigationService) {
      return;
    }

    setTimeout(() => {
      if (changes.gizmoDestinationInput?.currentValue != null && !this.changeHooked) {
        this.changeHooked = true;
        if(this.targetInputComponent.block instanceof DataSetInput) {
          this.statusChange(!this.targetInputComponent.block.settings.required || this.gizmoDestinationInput.value.length > 0 ? 'VALID' : 'INVALID');
        } else {
          this.statusChange(this.gizmoDestinationInput.valid ? 'VALID' : 'INVALID');
        }
        this.subs.add(this.gizmoDestinationInput
          .statusChanges
          .subscribe((status: 'VALID' | 'INVALID' | string) => {
            this.statusChange(status);
          }));
      }
    }, 20);
  }

  ngOnDestroy() {
    if(!this.blocksConfig.editable) {
      this.blocksConfig.submittable && this.navigationService?.unregisterTarget(this.elementRef.nativeElement.id);
    }
    this.subs.unsubscribe();
  }

  private statusChange(status: 'VALID' | 'INVALID' | string) {
    if(!this.blocksConfig.editable) {
      this.navigationService?.updateTargetValidity(this.elementRef.nativeElement.id, status === 'VALID');
    }
  }

  highlight() {
    this.elementRef.nativeElement.animate([
      {
        transform: 'scale(1)',
        offset: 0.01
      },
      {
        transform: 'scale(1.08)',
        offset: 0.50
      },
      {
        transform: 'scale(1)',
        offset: 0.99
      },
    ],
    {
      duration: 350,
      delay: 0,
      fill: 'both',
      easing: 'cubic-bezier(.5,0,0,.5)'
    });
  }
}
